import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Bell, AlertTriangle, Info, X } from 'lucide-react';
import { toast } from 'sonner';

const AnnouncementDisplay = () => {
  const { api, user } = useAuth();
  const { lastMessage } = useWebSocket();
  const [motdAnnouncement, setMotdAnnouncement] = useState(null);
  const [showMotd, setShowMotd] = useState(false);
  const [checkedMotd, setCheckedMotd] = useState(false);

  // Check for MOTD on login
  const checkMotd = useCallback(async () => {
    if (!user || checkedMotd) return;
    
    try {
      const response = await api().get('/announcements/motd');
      if (response.data) {
        setMotdAnnouncement(response.data);
        setShowMotd(true);
      }
      setCheckedMotd(true);
    } catch (error) {
      console.log('No MOTD available');
      setCheckedMotd(true);
    }
  }, [api, user, checkedMotd]);

  useEffect(() => {
    checkMotd();
  }, [checkMotd]);

  // Handle real-time announcements via WebSocket
  useEffect(() => {
    if (!lastMessage) return;
    
    if (lastMessage.type === 'announcement') {
      const announcement = lastMessage.data;
      
      // Check if user's role is targeted
      const targetRoles = announcement.target_roles || [];
      if (targetRoles.length > 0 && !targetRoles.includes(user?.role)) {
        return;
      }
      
      // Show toast for real-time announcements
      if (announcement.type === 'realtime') {
        showAnnouncementToast(announcement);
      } else if (announcement.type === 'motd') {
        // Show modal for MOTD sent via broadcast
        setMotdAnnouncement(announcement);
        setShowMotd(true);
      }
    }
  }, [lastMessage, user]);

  const showAnnouncementToast = (announcement) => {
    const priorityIcons = {
      low: Info,
      normal: Bell,
      high: AlertTriangle,
      urgent: AlertTriangle
    };
    
    const priorityStyles = {
      low: 'bg-gray-800',
      normal: 'bg-blue-900',
      high: 'bg-amber-900',
      urgent: 'bg-red-900'
    };
    
    const Icon = priorityIcons[announcement.priority] || Bell;
    
    toast.custom((t) => (
      <div className={`${priorityStyles[announcement.priority]} rounded-lg shadow-lg p-4 max-w-md border border-white/10`}>
        <div className="flex items-start gap-3">
          <div className="p-2 bg-white/10 rounded-lg">
            <Icon className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h4 className="font-semibold text-white">{announcement.title}</h4>
              <button 
                onClick={() => toast.dismiss(t)}
                className="text-white/50 hover:text-white"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="text-sm text-white/80 mt-1">{announcement.message}</p>
            {announcement.priority === 'urgent' && (
              <Badge className="mt-2 bg-red-500/50 text-white">Urgente</Badge>
            )}
          </div>
        </div>
      </div>
    ), {
      duration: announcement.priority === 'urgent' ? 10000 : 5000,
      position: 'top-right'
    });
  };

  const handleDismissMotd = async () => {
    if (motdAnnouncement) {
      try {
        await api().post(`/announcements/${motdAnnouncement.id}/dismiss`);
      } catch (error) {
        console.log('Could not dismiss announcement');
      }
    }
    setShowMotd(false);
    setMotdAnnouncement(null);
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      normal: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      high: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      urgent: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return colors[priority] || colors.normal;
  };

  if (!showMotd || !motdAnnouncement) return null;

  return (
    <Dialog open={showMotd} onOpenChange={setShowMotd}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-3 rounded-lg ${
              motdAnnouncement.priority === 'urgent' ? 'bg-red-500/20' : 'bg-primary/20'
            }`}>
              {motdAnnouncement.priority === 'urgent' ? (
                <AlertTriangle className="h-6 w-6 text-red-400" />
              ) : (
                <Bell className="h-6 w-6 text-primary" />
              )}
            </div>
            <div>
              <DialogTitle className="flex items-center gap-2">
                {motdAnnouncement.title}
                {motdAnnouncement.priority !== 'normal' && (
                  <Badge className={getPriorityColor(motdAnnouncement.priority)}>
                    {motdAnnouncement.priority === 'low' && 'Baja'}
                    {motdAnnouncement.priority === 'high' && 'Alta'}
                    {motdAnnouncement.priority === 'urgent' && 'Urgente'}
                  </Badge>
                )}
              </DialogTitle>
              <DialogDescription>
                Mensaje del Día
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <div className="py-4">
          <p className="text-sm whitespace-pre-wrap">{motdAnnouncement.message}</p>
          
          {motdAnnouncement.created_by_name && (
            <p className="text-xs text-muted-foreground mt-4">
              — {motdAnnouncement.created_by_name}
            </p>
          )}
        </div>
        
        <DialogFooter>
          <Button onClick={handleDismissMotd} className="btn-primary w-full">
            Entendido
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnnouncementDisplay;
